// Import necessary React and Material-UI components
import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  Fade,
  Grow,
  Backdrop
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { isAdminMode } from '../utils/auth'; // Authentication utility

// Teacher interface matching backend structure (with optional fields)
interface Teacher {
  id?: number;       // Optional for new teachers not yet saved
  name: string;      // Teacher's display name
  subject: string;   // Subject area expertise
  imageUrl: string;  // URL to teacher's profile image
}

// Base API URL from environment variables with fallback
const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const AwesomeTeachers: React.FC = () => {
  // State Management
  const [teachers, setTeachers] = useState<Teacher[]>([]);          // Original teacher data
  const [editableTeachers, setEditableTeachers] = useState<Teacher[]>([]); // Editable copy
  const [filesToUpload, setFilesToUpload] = useState<{ [key: number]: File }>({}); // Image files to upload
  const [loading, setLoading] = useState(true);                     // Loading state
  const [error, setError] = useState<string | null>(null);          // Error handling
  const [editMode, setEditMode] = useState(false);                  // Admin edit mode toggle
  const [saving, setSaving] = useState(false);                      // Save loading state
  const scrollRef = useRef<HTMLDivElement | null>(null);            // Reference for scroll container

  // Horizontal scroll handler
  function scroll(direction: 'left' | 'right') {
    const container = scrollRef.current;
    if (!container) return;
    const scrollAmount = 320;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }

  // Data Fetching Effect
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/api/teachers`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const data: Teacher[] = await response.json();
        setTeachers(data);
        setEditableTeachers(data);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load teachers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchTeachers();
  }, []);

  // Image Upload Handler
  const handleImageUpload = (index: number, file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setEditableTeachers(prev => {
      const updated = [...prev];
      updated[index].imageUrl = previewUrl;
      return updated;
    });
    setFilesToUpload(prev => ({ ...prev, [index]: file }));
  };

  // Text Field Change Handler
  const handleTextChange = (index: number, field: keyof Teacher, value: string) => {
    setEditableTeachers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Add New Teacher Card
  const addNewTeacher = () => {
    setEditableTeachers(prev => [...prev, {
      name: '',
      subject: '',
      imageUrl: ''
    }]);
  };

  // Save Handler (Complex operation with multiple steps)
  const handleSave = async () => {
    try {
      setSaving(true);
      // 1. Upload new images first
      const uploadResults = await Promise.all(
        Object.entries(filesToUpload).map(async ([indexStr, file]) => {
          const formData = new FormData();
          formData.append('image', file);
          const response = await fetch(`${BASE_URL}/api/teachers/upload`, {
            method: 'POST',
            body: formData,
          });
          if (!response.ok) throw new Error(`Upload failed: ${await response.text()}`);
          return { index: parseInt(indexStr), imageUrl: (await response.json()).imageUrl };
        })
      );

      // 2. Update teacher data with server URLs
      const updatedTeachers = editableTeachers.map((teacher, index) => ({
        ...teacher,
        imageUrl: uploadResults.find(u => u.index === index)?.imageUrl || teacher.imageUrl
      }));

      // 3. Prepare data for saving
      const saveData = updatedTeachers.map(teacher => ({
        Id: teacher.id || 0,
        Name: teacher.name || "Unnamed Teacher",
        Subject: teacher.subject || "General",
        ImageUrl: teacher.imageUrl
      }));

      // 4. Filter out invalid entries
      const validTeachers = saveData.filter(teacher =>
        teacher.Name.trim() !== "Unnamed Teacher" ||
        teacher.Subject.trim() !== "General"
      );

      // 5. Send batch update to server
      const saveResponse = await fetch(`${BASE_URL}/api/teachers/batch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(validTeachers),
      });
      if (!saveResponse.ok) throw new Error(`Save failed: ${await saveResponse.text()}`);

      // 6. Update local state with saved data
      const savedData = await saveResponse.json();
      setTeachers(savedData);
      setEditableTeachers(savedData);
      setFilesToUpload({});
      setEditMode(false);
    } catch (err: any) {
      console.error('Save error:', err);
      alert(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Cancel Edit Handler
  const handleCancel = () => {
    setEditableTeachers(teachers);
    setFilesToUpload({});
    setEditMode(false);
  };

  // Component Render
  return (
    <div className="w-full flex justify-center items-center  p-6">
      <div className="md:w-[80%] w-[90%]">
        <Box
          className="relative py-16 px-6 sm:px-12"
          sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            transition: 'all 0.3s ease-in-out'
          }}
        >
          {/* Loading Backdrop */}
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }}
            open={saving}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress color="inherit" size={40} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Saving changes...
              </Typography>
            </Box>
          </Backdrop>

          {/* Admin Control Bar */}
          {isAdminMode() && (
            <Fade in timeout={500}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  display: 'flex',
                  gap: 1.5,
                  zIndex: 10
                }}
              >
                {editMode ? (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={handleSave}
                      startIcon={<SaveIcon />}
                      disabled={saving}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        py: 1,
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)'
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCancel}
                      startIcon={<CancelIcon />}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        py: 1,
                        borderWidth: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          borderWidth: 2
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setEditMode(true)}
                    startIcon={<EditIcon />}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                      }
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </Fade>
          )}

          {/* Main Content */}
          <Fade in timeout={800}>
            <Box className="text-center mb-12">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Our Awesome Teachers
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                Meet our exceptional educators who inspire and guide students toward excellence every day.
              </Typography>
            </Box>
          </Fade>

          {/* Loading State */}
          {loading && (
            <Box className="flex justify-center items-center py-20">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <CircularProgress size={48} thickness={4} />
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Loading our amazing teachers...
                </Typography>
              </Box>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Fade in timeout={500}>
              <Box className="text-center py-12">
                <Typography variant="body1" color="error" sx={{ mb: 3, fontSize: '1.1rem' }}>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5
                  }}
                >
                  Try Again
                </Button>
              </Box>
            </Fade>
          )}

          {/* Teacher Cards Container */}
          {!loading && !error && (
            <Box
              ref={scrollRef}
              sx={{
                overflowX: 'auto',
                pb: 3,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollBehavior: 'smooth'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  minWidth: 'max-content',
                  px: 1
                }}
              >
                {editableTeachers.map((teacher, index) => (
                  <Grow
                    key={index}
                    in
                    timeout={300 + (index * 100)}
                    style={{ transformOrigin: 'center center' }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        width: 280,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: editMode ? 'default' : 'pointer',
                        '&:hover': {
                          transform: editMode ? 'none' : 'translateY(-8px) scale(1.02)',
                          boxShadow: editMode ? 'none' : '0 20px 40px rgba(0, 0, 0, 0.15)',
                          '& .teacher-image': {
                            transform: 'scale(1.05)'
                          }
                        }
                      }}
                    >
                      {/* Image Section */}
                      <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                        <Box
                          className="teacher-image"
                          component="img"
                          src={
                            teacher.imageUrl
                              ? teacher.imageUrl.startsWith('blob:')
                                ? teacher.imageUrl
                                : `${teacher.imageUrl}`
                              : '/default-teacher.jpg'
                          }
                          alt={teacher.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />

                        {/* Image Upload Overlay */}
                        {editMode && (
                          <Fade in timeout={300}>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0, 0, 0, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                                '&:hover': { opacity: 1 }
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                id={`image-upload-${index}`}
                                onChange={(e) => e.target.files?.[0] && handleImageUpload(index, e.target.files[0])}
                              />
                              <label htmlFor={`image-upload-${index}`}>
                                <IconButton
                                  component="span"
                                  sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </label>
                            </Box>
                          </Fade>
                        )}
                      </Box>

                      {/* Teacher Info Section */}
                      <Box sx={{ p: 3 }}>
                        {editMode ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              fullWidth
                              value={teacher.name}
                              onChange={(e) => handleTextChange(index, 'name', e.target.value)}
                              placeholder="Teacher name"
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                  fontSize: '1.1rem',
                                  fontWeight: 600
                                }
                              }}
                            />
                            <TextField
                              fullWidth
                              value={teacher.subject}
                              onChange={(e) => handleTextChange(index, 'subject', e.target.value)}
                              placeholder="Subject"
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                fontSize: '1.25rem',
                                color: 'text.primary',
                                mb: 0.5,
                                lineHeight: 1.3
                              }}
                            >
                              {teacher.name || 'New Teacher'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                opacity: 0.8
                              }}
                            >
                              {teacher.subject || 'Subject'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grow>
                ))}

                {/* Add New Teacher Card */}
                {editMode && (
                  <Grow in timeout={300 + (editableTeachers.length * 100)}>
                    <Paper
                      onClick={addNewTeacher}
                      sx={{
                        width: 280,
                        height: 320,
                        borderRadius: '20px',
                        border: '2px dashed rgba(102, 126, 234, 0.3)',
                        background: 'rgba(102, 126, 234, 0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: 'rgba(102, 126, 234, 0.5)',
                          background: 'rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      <AddPhotoAlternateIcon
                        sx={{
                          fontSize: 48,
                          color: 'rgba(102, 126, 234, 0.6)',
                          mb: 2
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'rgba(102, 126, 234, 0.8)',
                          fontWeight: 600
                        }}
                      >
                        Add New Teacher
                      </Typography>
                    </Paper>
                  </Grow>
                )}
              </Box>
            </Box>
          )}

          {/* Scroll Navigation */}
          {!loading && !error && teachers.length > 0 && (
            <Fade in timeout={1000}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 24,
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  zIndex: 5
                }}
              >
                <IconButton
                  onClick={() => scroll('left')}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <ArrowBackIcon sx={{ color: '#667eea' }} />
                </IconButton>
                <IconButton
                  onClick={() => scroll('right')}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <ArrowForwardIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Box>
            </Fade>
          )}
        </Box>
      </div>
    </div>
  );
};

export default AwesomeTeachers;